import React, {FunctionComponent, useCallback, useEffect, useMemo, useRef, useState} from "react";
import {fileService} from "../../../../services/FileService";
import CustomTable, {CustomTableRef} from "../CustomTable";
import {CustomTableColumn, CustomTableType} from "../../../../interfaces/TableInterfaces";
import usePaginatedData from "../../../../hooks/usePaginatedData";
import {DocumentDTO, DocumentSearchObject, DocumentsByTheme} from "../../../../interfaces/FileInterfaces";
import _, {Dictionary} from "lodash";
import DocumentTableRowTheme from "./DocumentTableRowTheme";

interface DocumentTableProps {
  className?: string,
  isAdmin?: boolean,
  isResource?: boolean,
  searchObject: DocumentSearchObject,
  DocumentRowComponent: FunctionComponent<{document: DocumentDTO}>,
}

const DocumentTable: FunctionComponent<DocumentTableProps> = ({
  className = "",
  isAdmin = false,
  isResource = false,
  searchObject,
  DocumentRowComponent,
}) => {
  const ref = React.useRef<CustomTableRef>();
  const observerRef = useRef<HTMLDivElement | null>(null);

  const {
    data: paginatedData,
    setPageSize,
    pageSize,
    page,
    sortOptions,
    setSortOptions,
  } = usePaginatedData(fileService.getMyDocuments, searchObject)

  const [data, setData] = useState<DocumentDTO[]>([]);

useEffect(() => {
  setData((prev) => {
    return page === 0 ? paginatedData : [...prev, ...paginatedData];
  });
}, [paginatedData, page]);
  
  const columns: CustomTableColumn[] = useMemo(() => isAdmin ?
    [
      {title: "documentary_base_table_column_name", accessor: "name", sortable: true},
      {title: "documentary_base_table_column_activities", accessor: "linkedActivities", sortable: true},
      {title: "documentary_base_table_column_profiles", accessor: "profileAccess", sortable: true},
      {title: "table_actions", accessor: "actions", sortable: false, className: "col-2"},
    ] : isResource ? [
      {title: "documentary_base_table_column_name", accessor: "name", sortable: true},
    ] : [
      {title: "documentary_base_table_column_name", accessor: "name", sortable: true},
      {title: "documentary_base_table_column_activities", accessor: "linkedActivities", sortable: true},
    ], [])

  const groupedData: Dictionary<DocumentDTO[]> = _.groupBy(data, "theme");
  const groupedDataList: DocumentsByTheme[] = Object.keys(groupedData).map(key => ({id: key, documents: groupedData[key]}))


  const handleScroll = useCallback(() => {
    if (!observerRef.current) return;
  
    const { scrollTop, scrollHeight, clientHeight } = observerRef.current;
    if (scrollTop + clientHeight >= scrollHeight - 10) {
      setPageSize(pageSize + 20);
    }
  }, [setPageSize]);

  useEffect(() => {
    const container = observerRef.current;
    if (!container) return;

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  return (
    <div className="scroller epow-custom-table-container">
      <div ref={observerRef} className={`theme ${className}`}>
        <CustomTable
          ref={ref}
          sortOptions={sortOptions}
          onSortOptions={setSortOptions}
          className={`${className}`}
          type={CustomTableType.DOCUMENTS_TABLE}
          datas={groupedDataList}
          columns={columns}
          RowComponent={({data, open, ...rest}) => {
            const documentsByTheme = data as DocumentsByTheme;
            return (
              <>
                <DocumentTableRowTheme theme={documentsByTheme.id} open={!open} {...rest} />
                {(!open && documentsByTheme.documents.length > 0) && documentsByTheme.documents.map((document) =>
                  <DocumentRowComponent key={`${documentsByTheme.id}-${document.id}`} document={document} />
                )}
              </>);
          }}
        />
      </div>
     </div>
  )
}

export default DocumentTable;
